import React from 'react';
import { RecentActivity } from '../types';
import { getTimeAgo, getInitials } from '../helpers';
import styles from '../../../../../../styles/OverviewTab.module.css';

interface RecentActivityListProps {
  activities: RecentActivity[];
  userRole: string;
}

const RecentActivityList: React.FC<RecentActivityListProps> = ({ activities, userRole }) => {
  // Get activity type icon and color based on description
  const getActivityType = (description: string) => {
    const lowerDesc = description.toLowerCase();
    
    if (lowerDesc.includes('completed') || lowerDesc.includes('finished')) {
      return {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
        colorClass: styles.activityTypeCompleted
      };
    }
    
    if (lowerDesc.includes('assigned') || lowerDesc.includes('created')) {
      return {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="12" y1="18" x2="12" y2="12"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
        ),
        colorClass: styles.activityTypeAssigned
      };
    }
    
    if (lowerDesc.includes('joined') || lowerDesc.includes('added')) {
      return {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="8.5" cy="7" r="4"></circle>
            <line x1="20" y1="8" x2="20" y2="14"></line>
            <line x1="23" y1="11" x2="17" y2="11"></line>
          </svg>
        ),
        colorClass: styles.activityTypeJoined
      };
    }
    
    if (lowerDesc.includes('earned') || lowerDesc.includes('points') || lowerDesc.includes('badge')) {
      return {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
        ),
        colorClass: styles.activityTypeReward
      };
    }
    
    if (lowerDesc.includes('approved') || lowerDesc.includes('reviewed')) {
      return {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 11 12 14 22 4"></polyline>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
        ),
        colorClass: styles.activityTypeApproved
      };
    }
    
    // Default
    return {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      colorClass: styles.activityTypeDefault
    };
  };

  return (
    <div className={styles.overviewCard}>
      <h3 className={styles.cardTitle}>
        <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
        Recent Activity
        
      </h3>
      
      <div className={styles.activityList}>
        {activities && activities.length > 0 ? (
          <ul>
            {activities.map((activity) => {
              const activityType = getActivityType(activity.description);
              return (
                <li key={activity.id} className={styles.activityItem}>
                  <div className={`${styles.activityAvatar} ${activityType.colorClass}`}>
                    {activityType.icon}
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityText}>
                      <strong>{activity.user}</strong> {activity.description}
                    </div>
                    <div className={styles.activityTime}>
                      {getTimeAgo(new Date(activity.timestamp))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles.emptyActivity}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            <p>No recent activity yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivityList;
